
import { DataMapper } from '@/api/mappers/interfaces';
import { AIStaffMember, AIStaffRole, AIStaffLevel, Achievement } from "@/lib/types";
import { Database } from '@/integrations/supabase/types';
import { Json } from '@/integrations/supabase/types';

// Define the database type for AI Staff
export type AIStaffDbFields = {
  id: string;
  name: string;
  role: Database['public']['Enums']['ai_staff_role'];
  level: Database['public']['Enums']['ai_staff_level'];
  experience: number;
  skill_points: number;
  abilities: string[];
  avatar?: string | null;
  hired_at: string;
  is_active: boolean;
  terminated_at?: string | null;
  achievements: Json;
  business_id?: string;
};

/**
 * Mapper implementation for AIStaffMember data
 */
export const staffMapper: DataMapper<AIStaffDbFields, AIStaffMember> = {
  toFrontend: (dbStaff: AIStaffDbFields): AIStaffMember => {
    let achievementsArray: Achievement[] = [];
    
    if (dbStaff.achievements) {
      const achievementsData = Array.isArray(dbStaff.achievements) 
        ? dbStaff.achievements 
        : typeof dbStaff.achievements === 'string' 
          ? JSON.parse(dbStaff.achievements as string) 
          : dbStaff.achievements;
          
      if (Array.isArray(achievementsData)) {
        achievementsArray = achievementsData.map((achievement: any) => ({
          id: String(achievement.id || ''),
          name: String(achievement.name || ''),
          description: String(achievement.description || ''),
          icon: String(achievement.icon || ''),
          unlockedAt: achievement.unlockedAt ? new Date(achievement.unlockedAt) : undefined,
          isUnlocked: Boolean(achievement.isUnlocked)
        }));
      }
    }
    
    return {
      id: dbStaff.id,
      name: dbStaff.name,
      role: dbStaff.role as AIStaffRole,
      level: dbStaff.level as AIStaffLevel,
      experience: Number(dbStaff.experience),
      skillPoints: Number(dbStaff.skill_points),
      abilities: Array.isArray(dbStaff.abilities) ? dbStaff.abilities : [],
      avatar: dbStaff.avatar || undefined,
      hiredAt: new Date(dbStaff.hired_at),
      is_active: dbStaff.is_active,
      terminated_at: dbStaff.terminated_at ? new Date(dbStaff.terminated_at) : undefined,
      terminatedAt: dbStaff.terminated_at ? new Date(dbStaff.terminated_at) : undefined,
      achievements: achievementsArray,
      businessId: dbStaff.business_id
    };
  },
  
  toDb: (frontendData: AIStaffMember): AIStaffDbFields => {
    return {
      id: frontendData.id,
      name: frontendData.name,
      role: frontendData.role as Database['public']['Enums']['ai_staff_role'],
      level: frontendData.level as Database['public']['Enums']['ai_staff_level'],
      experience: frontendData.experience,
      skill_points: frontendData.skillPoints,
      abilities: frontendData.abilities,
      avatar: frontendData.avatar || null,
      hired_at: frontendData.hiredAt.toISOString(),
      is_active: frontendData.is_active ?? true,
      terminated_at: frontendData.terminatedAt?.toISOString() || null,
      achievements: frontendData.achievements.map(ach => ({
        id: ach.id,
        name: ach.name,
        description: ach.description,
        icon: ach.icon,
        unlockedAt: ach.unlockedAt?.toISOString(),
        isUnlocked: ach.isUnlocked
      })) as Json,
      business_id: frontendData.businessId
    };
  }
};

/**
 * Legacy mapper function for backward compatibility
 * Use staffMapper.toFrontend instead for new code
 */
export const mapToAIStaffMember = (dbStaff: any): AIStaffMember => {
  return staffMapper.toFrontend(dbStaff as AIStaffDbFields);
};
